import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Gift, Check, X, Inbox } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const statusColor = {
  requested: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  declined: "bg-red-100 text-red-700",
};
const blank = { title: "", description: "", fee: 0, available: true };

export default function UpgradesPage() {
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([
    api.get("/upgrades").then((r) => setItems(r.data)),
    api.get("/upgrade-requests").then((r) => setRequests(r.data)),
  ]).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(blank); };
  const openEdit = (u) => { setEditing(u.id); setForm({ title: u.title, description: u.description || "", fee: u.fee, available: u.available }); };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Please enter a title");
    setSaving(true);
    try {
      const payload = { ...form, fee: parseFloat(form.fee) || 0 };
      if (editing === "new") await api.post("/upgrades", payload);
      else await api.put(`/upgrades/${editing}`, payload);
      toast.success("Upgrade saved");
      setEditing(null);
      load();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/upgrades/${id}`);
    toast.success("Upgrade removed");
    load();
  };

  const setStatus = async (rid, status) => {
    await api.put(`/upgrade-requests/${rid}`, { status });
    toast.success(status === "confirmed" ? "Confirmed & billed to folio" : "Request declined");
    load();
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-accent" />;

  return (
    <div className="space-y-8" data-testid="upgrades-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Stay Upgrades</h1>
          <p className="text-muted-foreground text-sm">Offer pre-check-in upgrades — guests request, you confirm and bill to their room folio.</p>
        </div>
        <Button onClick={openNew} data-testid="add-upgrade-btn" className="bg-primary text-primary-foreground gap-1.5 shrink-0"><Plus className="w-4 h-4" /> Add upgrade</Button>
      </div>

      <div className="grid gap-3" data-testid="upgrades-list">
        {items.length === 0 ? (
          <div className="border border-border rounded-md bg-card text-center py-14"><Gift className="w-7 h-7 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No upgrades yet. Add early check-in, late checkout, a gift basket and more.</p></div>
        ) : items.map((u) => (
          <div key={u.id} className="border border-border rounded-md bg-card p-5 flex items-center justify-between gap-4" data-testid={`upgrade-${u.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-medium text-foreground">{u.title}</p>
                {!u.available && <Badge variant="secondary" className="text-xs">Hidden</Badge>}
              </div>
              {u.description && <p className="text-sm text-muted-foreground mt-0.5">{u.description}</p>}
              <p className="font-serif text-lg mt-1 text-accent">£{Number(u.fee).toFixed(2)}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="icon" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-upgrade-${u.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="outline" onClick={() => remove(u.id)} data-testid={`delete-upgrade-${u.id}`} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-serif text-xl text-foreground mb-3">Upgrade requests</h2>
        <div className="border border-border rounded-md bg-card divide-y divide-border" data-testid="upgrade-requests-list">
          {requests.length === 0 ? (
            <div className="text-center py-12"><Inbox className="w-7 h-7 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No requests yet.</p></div>
          ) : requests.map((r) => (
            <div key={r.id} className="p-4 flex items-center justify-between gap-4" data-testid={`upgrade-request-${r.id}`}>
              <div className="flex-1">
                <p className="font-medium text-foreground text-sm">{r.title} · <span className="text-accent">£{Number(r.fee).toFixed(2)}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">{r.guest_name}{r.room ? ` · Room ${r.room}` : ""} · {new Date(r.created_at).toLocaleString()}</p>
              </div>
              {r.status === "requested" ? (
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => setStatus(r.id, "confirmed")} data-testid={`confirm-request-${r.id}`} className="bg-primary text-primary-foreground gap-1"><Check className="w-4 h-4" /> Confirm</Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "declined")} data-testid={`decline-request-${r.id}`} className="gap-1"><X className="w-4 h-4" /> Decline</Button>
                </div>
              ) : (
                <Badge className={`${statusColor[r.status] || "bg-secondary"} border-0 capitalize shrink-0`} data-testid={`request-status-${r.id}`}>{r.status}</Badge>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="upgrade-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing === "new" ? "Add upgrade" : "Edit upgrade"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="upgrade-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Early check-in" className="mt-1.5" /></div>
            <div><Label>Description</Label><Textarea data-testid="upgrade-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Arrive from 11am and settle in early." className="mt-1.5" /></div>
            <div><Label>Fee (£)</Label><Input data-testid="upgrade-fee" type="number" step="0.01" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} className="mt-1.5" /></div>
            <div className="flex items-center justify-between pt-1">
              <Label>Available to guests</Label>
              <Switch checked={!!form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="upgrade-available" />
            </div>
            <Button onClick={save} disabled={saving} data-testid="save-upgrade-btn" className="w-full bg-primary text-primary-foreground">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save upgrade"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
